import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect } from "react";
import { cn } from "src/common/utils";
import { Button } from "src/components/ui/button";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import GithubIcon from "src/components/ui/icons/github";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";
import { UIEditSection } from "src/components/ui/UIEditSection";
import { ReviewSection } from "src/components/wizard";

import {
  APPROVAL_TYPE_LABELS,
  type ApprovalTypeOption,
  CAPABILITY_TYPE_LABELS,
  getDefaultCapabilityName,
  type Parameter,
  type WizardState,
} from "../types";

interface IntegrationNode {
  id: string;
  name: string;
  integrationType: string;
  githubAccountName: string | null | undefined;
  buildkiteOrgSlug: string | null | undefined;
}

interface Step4ReviewProps {
  state: WizardState;
  onUpdate: (updates: Partial<WizardState>) => void;
  githubIntegrations: IntegrationNode[];
  buildkiteIntegrations: IntegrationNode[];
}

// Parameter row component for editable parameters
const ParameterRow: React.FC<{
  param: Parameter;
  depth?: number;
  expandedParams: Set<string>;
  onToggleExpand: (paramId: string) => void;
  onParameterChange: (id: string, field: keyof Parameter, value: unknown) => void;
  onRemoveParameter: (id: string) => void;
  onAddNestedParameter: (parentId: string) => void;
}> = React.memo(function ParameterRow({
  param,
  depth = 0,
  expandedParams,
  onToggleExpand,
  onParameterChange,
  onRemoveParameter,
  onAddNestedParameter,
}) {
  const isExpanded = expandedParams.has(param.id);

  return (
    <>
      <tr className={cn("border-t border-border", depth > 0 && "bg-card/30")}>
        <td className="py-2 px-3">
          <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 20}px` }}>
            {param.type === "object" && (
              <button
                onClick={() => onToggleExpand(param.id)}
                className="text-muted-foreground hover:text-foreground"
                type="button"
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            )}
            <Input
              value={param.name}
              onChange={(e) => onParameterChange(param.id, "name", e.target.value)}
              disabled={param.locked}
              placeholder="name"
              className={cn(
                "h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0",
                param.locked && "text-muted-foreground"
              )}
            />
            {param.locked && <span className="text-xs text-muted-foreground">Preset</span>}
          </div>
        </td>

        <td className="py-2 px-3">
          <Select
            value={param.type}
            onValueChange={(v) => onParameterChange(param.id, "type", v)}
            disabled={param.locked}
          >
            <SelectTrigger className="h-7 text-sm border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">String</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
              <SelectItem value="select">Select</SelectItem>
              <SelectItem value="object">Object</SelectItem>
            </SelectContent>
          </Select>
        </td>

        <td className="py-2 px-3 text-center">
          <input
            type="checkbox"
            checked={param.required}
            onChange={(e) => onParameterChange(param.id, "required", e.target.checked)}
            disabled={param.locked}
            className="rounded"
          />
        </td>

        <td className="py-2 px-3">
          {param.type !== "object" && (
            <Input
              value={param.defaultValue || ""}
              onChange={(e) => onParameterChange(param.id, "defaultValue", e.target.value)}
              placeholder="-"
              className="h-7 text-sm border-border"
            />
          )}
        </td>

        <td className="py-2 px-3">
          <Textarea
            value={param.description}
            onChange={(e) => onParameterChange(param.id, "description", e.target.value)}
            placeholder="Description..."
            className="min-h-[28px] text-sm border-border resize-none"
            rows={1}
            style={{
              height: "auto",
              minHeight: "28px",
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = target.scrollHeight + "px";
            }}
          />
        </td>

        <td className="py-2 px-3 text-right">
          {!param.locked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemoveParameter(param.id)}
              className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
              type="button"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </td>
      </tr>

      {param.type === "select" && (
        <tr className="border-t border-border/50">
          <td colSpan={6} className="py-2 px-3 bg-card/20">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Options:</span>
              <Input
                placeholder="option1, option2, option3"
                value={param.selectOptions?.join(", ") || ""}
                onChange={(e) =>
                  onParameterChange(
                    param.id,
                    "selectOptions",
                    e.target.value.split(",").map((s) => s.trim())
                  )
                }
                className="h-7 text-sm flex-1 border-border"
              />
            </div>
          </td>
        </tr>
      )}

      {param.type === "object" && isExpanded && (
        <>
          {param.properties && param.properties.length > 0 ? (
            param.properties.map((nestedParam) => (
              <ParameterRow
                key={nestedParam.id}
                param={nestedParam}
                depth={depth + 1}
                expandedParams={expandedParams}
                onToggleExpand={onToggleExpand}
                onParameterChange={onParameterChange}
                onRemoveParameter={onRemoveParameter}
                onAddNestedParameter={onAddNestedParameter}
              />
            ))
          ) : (
            <tr className="border-t border-border/50">
              <td colSpan={6} className="py-3 px-3 text-center bg-card/20">
                <span className="text-xs text-muted-foreground">No nested properties defined</span>
                {param.customizable && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddNestedParameter(param.id)}
                    className="ml-3 h-6 text-xs"
                    type="button"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Property
                  </Button>
                )}
              </td>
            </tr>
          )}
          {param.customizable && param.properties && param.properties.length > 0 && (
            <tr className="border-t border-border/50">
              <td colSpan={6} className="py-2 px-3 bg-card/20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddNestedParameter(param.id)}
                  className="h-6 text-xs text-muted-foreground"
                  type="button"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add nested property
                </Button>
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
});

export function Step4Review({
  state,
  onUpdate,
  githubIntegrations,
  buildkiteIntegrations,
}: Step4ReviewProps) {
  // Get the selected GitHub workflow name from the stored workflows
  const selectedWorkflowName = React.useMemo(() => {
    if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && state.selectedGitHubWorkflow) {
      const workflow = state.githubWorkflows.find((w) => w.id === parseInt(state.selectedGitHubWorkflow, 10));
      return workflow?.name || state.selectedGitHubWorkflow;
    }
    return null;
  }, [state.capabilityType, state.selectedGitHubWorkflow, state.githubWorkflows]);

  // Get the selected GitHub repo name from the stored repos
  const selectedRepoName = React.useMemo(() => {
    if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && state.selectedGitHubRepo) {
      const repo = state.githubRepos.find((r) => r.id === parseInt(state.selectedGitHubRepo, 10));
      return repo?.full_name || state.selectedGitHubRepo;
    }
    return null;
  }, [state.capabilityType, state.selectedGitHubRepo, state.githubRepos]);

  // Get the selected Buildkite pipeline name from the stored pipelines
  const selectedPipelineName = React.useMemo(() => {
    if (state.capabilityType === "BUILDKITE_PIPELINE" && state.selectedBuildkitePipeline) {
      const pipeline = state.buildkitePipelines.find((p) => p.slug === state.selectedBuildkitePipeline);
      return pipeline?.name || state.selectedBuildkitePipeline;
    }
    return null;
  }, [state.capabilityType, state.selectedBuildkitePipeline, state.buildkitePipelines]);

  // Set default name only once when first entering the review step
  useEffect(() => {
    if (!state.hasSetDefaultName) {
      const defaultName = getDefaultCapabilityName(
        state.capabilityType,
        selectedWorkflowName || undefined,
        selectedPipelineName || undefined
      );
      if (defaultName) {
        onUpdate({
          name: defaultName,
          hasSetDefaultName: true,
        });
      }
    }
  }, [state.hasSetDefaultName, state.capabilityType, selectedWorkflowName, selectedPipelineName, onUpdate]);

  // Get selected integration details
  const selectedGithubIntegration = React.useMemo(() => {
    if (state.selectedGitHubOrgId) {
      return githubIntegrations.find((integration) => integration.id === state.selectedGitHubOrgId);
    }
    return githubIntegrations.length === 1 ? githubIntegrations[0] : undefined;
  }, [githubIntegrations, state.selectedGitHubOrgId]);

  const selectedBuildkiteIntegration = React.useMemo(() => {
    if (state.selectedBuildkiteOrgId) {
      return buildkiteIntegrations.find((integration) => integration.id === state.selectedBuildkiteOrgId);
    }
    return buildkiteIntegrations.length === 1 ? buildkiteIntegrations[0] : undefined;
  }, [buildkiteIntegrations, state.selectedBuildkiteOrgId]);

  // Parameter handlers
  const handleAddParameter = useCallback(() => {
    const newParam: Parameter = {
      id: `param-${Date.now()}`,
      name: "",
      type: "string",
      description: "",
      required: false,
    };
    onUpdate({ parameters: [...state.parameters, newParam] });
  }, [state.parameters, onUpdate]);

  const handleAddNestedParameter = useCallback(
    (parentId: string) => {
      const newParam: Parameter = {
        id: `nested-${Date.now()}`,
        name: "",
        type: "string",
        description: "",
        required: false,
      };

      const addNested = (params: Parameter[]): Parameter[] => {
        return params.map((p) => {
          if (p.id === parentId) {
            return { ...p, properties: [...(p.properties || []), newParam] };
          } else if (p.properties) {
            return { ...p, properties: addNested(p.properties) };
          }
          return p;
        });
      };

      onUpdate({ parameters: addNested(state.parameters) });
    },
    [state.parameters, onUpdate]
  );

  const handleRemoveParameter = useCallback(
    (id: string) => {
      const removeParam = (params: Parameter[]): Parameter[] => {
        return params
          .filter((p) => p.id !== id)
          .map((p) => ({
            ...p,
            properties: p.properties ? removeParam(p.properties) : undefined,
          }));
      };
      onUpdate({ parameters: removeParam(state.parameters) });
    },
    [state.parameters, onUpdate]
  );

  const handleParameterChange = useCallback(
    (id: string, field: keyof Parameter, value: unknown) => {
      const updateParam = (params: Parameter[]): Parameter[] => {
        return params.map((p) => {
          if (p.id === id) {
            return { ...p, [field]: value };
          } else if (p.properties) {
            return { ...p, properties: updateParam(p.properties) };
          }
          return p;
        });
      };
      onUpdate({ parameters: updateParam(state.parameters) });
    },
    [state.parameters, onUpdate]
  );

  const toggleExpanded = useCallback(
    (paramId: string) => {
      const newExpanded = new Set(state.expandedParams);
      if (newExpanded.has(paramId)) {
        newExpanded.delete(paramId);
      } else {
        newExpanded.add(paramId);
      }
      onUpdate({ expandedParams: newExpanded });
    },
    [state.expandedParams, onUpdate]
  );

  // Get display values for pipeline details
  const getPipelineDetails = () => {
    if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW") {
      return {
        type: "GitHub Actions Workflow",
        account: selectedGithubIntegration?.githubAccountName || "Unknown",
        repository: selectedRepoName || "Not selected",
        workflow: selectedWorkflowName || "Not selected",
      };
    }
    return {
      type: "Buildkite Pipeline",
      account: selectedBuildkiteIntegration?.buildkiteOrgSlug || "Unknown",
      pipeline: selectedPipelineName || "Not selected",
    };
  };

  const pipelineDetails = getPipelineDetails();

  return (
    <div className="space-y-6">
      {/* Name - Primary editable field */}
      <div>
        <Label htmlFor="capability-name" className="text-sm font-medium text-secondary-foreground">
          Name
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="capability-name"
          value={state.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="e.g. Deploy to Production"
          className="bg-card border-border text-foreground mt-2"
        />
        <p className="text-xs text-muted-foreground mt-2">
          Give your capability a descriptive name to identify it easily.
        </p>
      </div>

      {/* Pipeline Details Section - Read only */}
      <ReviewSection title="Pipeline Details">
        <div className="py-3 ">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</label>
          <div className="mt-1 flex items-center gap-2">
            {state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" ? (
              <GithubIcon className="h-4 w-4 text-muted-foreground" />
            ) : (
              <BuildkiteIcon className="h-4 w-4 text-muted-foreground" />
            )}
            <span>{CAPABILITY_TYPE_LABELS[state.capabilityType]}</span>
          </div>
        </div>
        <div className="py-3 ">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account</label>
          <div className="mt-1 text-muted-foreground">{pipelineDetails.account}</div>
        </div>
        {state.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && (
          <>
            <div className="py-3 ">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Repository</label>
              <div className="mt-1 text-muted-foreground">{pipelineDetails.repository}</div>
            </div>
            <div className="py-3">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Workflow</label>
              <div className="mt-1 text-muted-foreground">{pipelineDetails.workflow}</div>
            </div>
          </>
        )}
        {state.capabilityType === "BUILDKITE_PIPELINE" && (
          <div className="py-3">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pipeline</label>
            <div className="mt-1 text-muted-foreground">{pipelineDetails.pipeline}</div>
          </div>
        )}
      </ReviewSection>

      {/* Parameters - Expandable */}
      <UIEditSection
        title="Parameters"
        summary={
          <span className="text-muted-foreground">
            {state.parameters.length === 0
              ? "No parameters defined"
              : `${state.parameters.length} parameter${state.parameters.length === 1 ? "" : "s"} defined`}
          </span>
        }
      >
        {state.parameters.length === 0 ? (
          <div className="text-center py-4 border border-dashed border-border rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">No parameters defined</p>
            <Button variant="outline" size="sm" onClick={handleAddParameter} type="button">
              <Plus className="h-4 w-4 mr-2" />
              Add Parameter
            </Button>
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-card/50 text-xs text-muted-foreground">
                <tr>
                  <th className="text-left py-2 px-3 font-medium w-[180px]">Name</th>
                  <th className="text-left py-2 px-3 font-medium w-[100px]">Type</th>
                  <th className="text-center py-2 px-3 font-medium w-[60px]">Req</th>
                  <th className="text-left py-2 px-3 font-medium w-[100px]">Default</th>
                  <th className="text-left py-2 px-3 font-medium">Description</th>
                  <th className="py-2 px-3 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {state.parameters.map((param) => (
                  <ParameterRow
                    key={param.id}
                    param={param}
                    expandedParams={state.expandedParams}
                    onToggleExpand={toggleExpanded}
                    onParameterChange={handleParameterChange}
                    onRemoveParameter={handleRemoveParameter}
                    onAddNestedParameter={handleAddNestedParameter}
                  />
                ))}
              </tbody>
            </table>
            <div className="border-t border-border p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddParameter}
                className="w-full text-xs text-muted-foreground"
                type="button"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add parameter
              </Button>
            </div>
          </div>
        )}
      </UIEditSection>

      {/* Description - Expandable */}
      <UIEditSection
        title="Description"
        summary={
          <span className="text-muted-foreground truncate max-w-[400px] block">
            {state.description || "No description provided"}
          </span>
        }
      >
        <Textarea
          id="review-description"
          value={state.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Describe what this pipeline does..."
          rows={3}
          className="font-mono text-sm bg-card border-border"
        />
      </UIEditSection>

      {/* Approval Type - Expandable */}
      <UIEditSection
        title="Approval Type"
        summary={<span className="text-muted-foreground">{APPROVAL_TYPE_LABELS[state.approvalType]}</span>}
      >
        <Select value={state.approvalType} onValueChange={(v) => onUpdate({ approvalType: v as ApprovalTypeOption })}>
          <SelectTrigger className="bg-card border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(APPROVAL_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </UIEditSection>
    </div>
  );
}
